import React from 'react';
import { IconButton, Tooltip, Box, useMediaQuery, useTheme, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useThemeMode } from '@/colors';
import Brightness4Icon from '@mui/icons-material/NightsStay'; // Moon icon
import Brightness7Icon from '@mui/icons-material/Brightness7'; // Sun icon
import PaletteIcon from '@mui/icons-material/Palette'; // Theme indicator icon

interface ThemeToggleSwitchProps {
  compact?: boolean;
  iconOnly?: boolean;
}

const ThemeToggleSwitch: React.FC<ThemeToggleSwitchProps> = ({ compact = false, iconOnly = false }) => {
  const { mode, toggleTheme, colors } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Mobile version with button group and theme indicator
  if (!iconOnly) {
    const handleThemeChange = (event: React.MouseEvent<HTMLElement>, newTheme: string | null) => {
      if (newTheme && newTheme !== mode) {
        toggleTheme();
      }
    };

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        ml: compact ? 0 : 2,
        gap: 1
      }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleThemeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${colors.background.neutral.mid}`,
              color: colors.text.neutral.mid,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: colors.background.neutral.lighter,
              },
              '&.Mui-selected': {
                backgroundColor: colors.background.brand.dark,
                color: colors.text.neutral.lightest,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: colors.background.brand.dark,
                },
              },
            },
          }}
          aria-label="theme selection"
        >
          <ToggleButton value="light" aria-label="light theme">
            Light
          </ToggleButton>
          <ToggleButton value="dark" aria-label="dark theme">
            Dark
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );
  }

  // Desktop version with icon button
  return (
    <Tooltip title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}>
      <IconButton
        onClick={toggleTheme}
        sx={{
          color: colors.text.neutral.mid,
          ml: compact ? 0 : 2,
        }}
        aria-label="toggle theme"
      >
        {mode === 'dark' ? <Brightness4Icon /> : <Brightness7Icon />}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggleSwitch;
